import { Injectable, EventEmitter } from '@angular/core';
import { Router } from '@angular/router';
import { AuthService, PrivilegesLoadingService } from '@auth';
import { ThemeService } from '@themes';
import { TenantService } from '@tenant';
import { GlobalEventBus, ActionEvents, TabEvents, TabEventBus } from '@event-bus';
import { RoutingObserverService, RoutingInfoService, RoutingModifiyerService } from '@routing';
import { Config } from '@config';
import { RouteActionsService } from './routeactions.service';
import { ExternalActionsService } from './external.actions.service';
import { LoaderService } from '@loader';
import { MessagesService, MessageType } from '@ui-messages';
import { __i18n } from '@localisation';
import { FullScreenService, PopupManagerService } from '@screen';
import { CachedPromiseManager } from '@cache';
import { WorkflowMessagesService } from './workflow.messages';
import { UserService } from '@user';
import { StateService, StateListener, States, StateHandler } from '@state';


@Injectable( { providedIn : 'root'})
export class InitWorkflowService {
    constructor(
        private authService: AuthService,
        private theme: ThemeService,
        private tenant: TenantService,
        private eventBus: GlobalEventBus,
        private routingObserver: RoutingObserverService,
        private routingInfo: RoutingInfoService,
        private config: Config,
        private routeActionsService: RouteActionsService,
        private actionsService: ExternalActionsService,
        public router: Router,
        private loader: LoaderService,
        private messages: MessagesService,
        private tabEventBus: TabEventBus,
        private popupManager: PopupManagerService,
        private workflowMessage: WorkflowMessagesService,
        private userService: UserService,
        private stateService: StateService,
        private privilegesLoadingService: PrivilegesLoadingService,
        private routingModifiyer: RoutingModifiyerService,
        private themeService: ThemeService,
        private fullsScreenService: FullScreenService
    ) {
        const stateHandler = new StateHandler(this);
        //load theme
        this.init();
    }

    private async init(){
        //check login status
        this.authService.checkLoginStatus(() => {
            if (this.config.serverConfig && this.config.serverConfig.VERIFICATION_CODE_REQUIRED) {
                this.stateService.change(States.isLoggedIn, false);
                this.router.navigateByUrl('/VerificationCode');
            } else {
                this.authService.logIn();
            }
        });

        document.querySelector('body').addEventListener('click', () => {
            this.checkUserTimeout();
        });

        this.addIframeListener();


        this.routingObserver.onRouteChanged.subscribe( async(config: any) => {
            //remove all popups
            this.popupManager.removeAllPopups();

            this.checkUserTimeout();

            this.addIframeListener();

            const currentRoutingEntry = this.routingInfo.getCurrentConfig();

            //if user is logged in an the routing conf entry ignorIfLoggedIn is set, jump to Home
            if (this.authService.isLogginStarted && currentRoutingEntry.data.ignorIfLoggedIn) {
                this.router.navigateByUrl('/Home');
            }

            //check if a route is in the logged out area
            if (
                this.routingInfo.isLoggedOutPath(config.url) &&
                this.authService.isLogginStarted &&
                (!currentRoutingEntry.data || !currentRoutingEntry.data.stopAutoLoggout)
            ) {
                this.authService.disconnect();
            }

            const user = this.userService.getUser();
            if (user && user.mustChangePassword) {
                this.router.navigateByUrl(this.config.changePasswordUrl);
            }
        });

        //if user must change passwort redirect to change password screen
        this.eventBus.on(ActionEvents.userLoaded, (user: any) => {
            if (user && user.mustChangePassword) {
                this.router.navigateByUrl(this.config.changePasswordUrl);
            }
        });

        //handle unkown errors
        this.eventBus.on(ActionEvents.unknownError, (e: any) => {
            console.error('An unkown error occured! [InitWorkflowService.constructor]', e);
            this.loader.stopAll();
            this.messages.addMessage({
                type: MessageType.error,
                text: __i18n('An unknown error occured!', 'global.message.unknown.error')
            });
        });

        this.eventBus.on(ActionEvents.disconnect, () => {
            //delete all caches adter disconnect
            CachedPromiseManager.removeFullCache();
        });

        this.eventBus.on(ActionEvents.activatePlant, () => {
            //trigger tab login event after the plant was activated
            this.tabEventBus.trigger(TabEvents.login);
            this.tabEventBus.trigger(TabEvents.plantActivated);
        });

        this.tabEventBus.on(TabEvents.plantActivated, async() => {
            await this.privilegesLoadingService.refreshPrivileges();
            this.router.navigateByUrl('Administration/Plants');
        });

        this.tabEventBus.on(TabEvents.refresh, async() => {
            location.reload();
        });

        this.tenant.onTenantChange.subscribe((tenant) => {
            const configFromSnapshot = this.routingInfo.getRouterConfigFromSnapshot(this.router.routerState.snapshot);
            if (configFromSnapshot && configFromSnapshot.data && configFromSnapshot.data.redirectRouteOnTenantSwitch){
                this.router.navigateByUrl(configFromSnapshot.data.redirectRouteOnTenantSwitch);
            }
        });

        window.addEventListener('unload', () => {
            localStorage.removeItem('_lastroute_' + this.config.instanceId);
        });
    }

    public webInit(){
        setTimeout(() => {
            this.addIframeListener();
        }, 3000);
    }

    private iframeElement;
    private async addIframeListener(){

        this.iframeElement = document.querySelector('iframe');
        if(this.iframeElement){
            this.iframeElement.addEventListener('load', () => {
                this.iframeElement.contentWindow.document.body.addEventListener('click', () => {
                    this.checkUserTimeout();
                });
            })
        }
    }

    private userTimeout: any;
    private isThemeLoaded: boolean = false;
    private isLoggedIn: boolean = false;
    private isTenantReady: boolean = false;

    private checkLogOutTimeout() {
        const currentTime = new Date().getTime();
        const lastActivity = Number(localStorage.getItem('_lastActivity'));
        if (currentTime - this.config.services.auth.inActiveTimeout >= lastActivity) {
            this.authService.disconnect({timedOut: true});
        } else {
            clearTimeout(this.userTimeout);
            this.userTimeout = setTimeout(
                this.checkLogOutTimeout.bind(this),
                this.config.services.auth.inActiveTimeout
            );
        }
    }

    private checkUserTimeout(){
        localStorage.setItem('_lastActivity', String(new Date().getTime()));
        //check user activity
        clearTimeout(this.userTimeout);
        this.checkLogOutTimeout();
    }

    public onReady: EventEmitter<boolean> = new EventEmitter();

    //Checks if the app is ready to start
    @StateListener([States.themeLoaded, States.isLoggedIn, States.tenant],  { init : true})
    public checkIfReady(value: any){
        if(value.themeLoaded && value.isLoggedIn && value.tenant){
            this.onReady.next(true);
        }
    }

    /**
     * checks if the user has to change his password, if so it redirects to the change password page
     */
    @StateListener([States.userMustChangePassword], { init: true, unsubscribeTruthy: true })
    public checkMustChangePassword(values: any) {
        if (values.mustChangePassword === true) {
            this.router.navigateByUrl(this.config.changePasswordUrl);
        }else if(values.mustChangePassword === false){
            this.tabEventBus.trigger(TabEvents.login);
            return true;
        }
    }

    //check if user must register
    @StateListener([States.currentRouteUrl, States.userMustRegister], { init: true, unsubscribeTruthy: true })
    public checkRegistration(values: any) {
        this.addIframeListener()
        if (values.currentRouteUrl === '/Login' && values.userMustRegister === true) {
            this.router.navigateByUrl('/ExtLoginRegistrationInfo');
        }

        if (values.userMustRegister === false) {
            return true;
        }
    }

    //check if user must enter a verification code
    @StateListener([States.isLoginable, States.userMustEnterVerificationCode], { init: true, unsubscribeTruthy: true })
    public checkIfLoginable(values: any) {
        if (values.isLoginable && !values.userMustEnterVerificationCode) {
            return true;
        } else if (values.isLoginable && values.userMustEnterVerificationCode) {
            this.router.navigateByUrl('/VerificationCode');
        }
    }

    private navigationTimer: any;

    //check if the user must activate a plant
    @StateListener([
        States.isLoggedIn,
        States.userMustActivatePlant,
        States.user,
        States.routingStarted,
        States.isRedirectedAfterLogin,
        States.userMustChangePassword,
        States.currentRouteUrl
    ], { init: true, unsubscribeTruthy: true })
    public mustActivatePlant(values: any) {
        if (
            values.isLoggedIn
            && values.userMustActivatePlant
            && !values.userMustChangePassword
            && !values.routingStarted
            && values.currentRouteUrl !== '/ActivatePlant'
        ) {
            clearTimeout(this.navigationTimer);
            this.navigationTimer = setTimeout(() => this.router.navigateByUrl('/ActivatePlant'), 20);
        }

        if(values.isLoggedIn && values.user && !values.userMustActivatePlant){
            // do not remove listener to avoid problems with relogin
            //return true;
        }
    }

    //check which outlet should be active
    @StateListener([States.isLoggedIn, States.isLoggedOutPath, States.themeIsLoading, States.tenant], { init: true })
    public checkCurrentOutlet(values: any) {
        if(values.themeIsLoading){
            this.stateService.change(States.isLoggedOutOutlet, null);
            return;
        }
        if (values.isLoggedOutPath) {
            this.stateService.change(States.isLoggedOutOutlet, true);
        } else if (values.isLoggedIn && !values.isLoggedOutPath && States.tenant) {
            this.stateService.change(States.isLoggedOutOutlet, false);
        }else{
            this.stateService.change(States.isLoggedOutOutlet, null);
        }
    }

    private isLoadingTheme: string;
    //check the initial Theme loading
    @StateListener([
        States.isLoggedIn,
        States.themeConfig,
        States.tenant,
        States.isLoggedOutPath,
        States.domainPatternFileIsLoaded
    ])
    public async checkLoginAndLoadTheme(values: any){

        //check theme loading only if the logged-in check is ready
        if(values.isLoggedIn === undefined){
            return;
        }

        let themeToLoad = this.theme.getInitialTheme(values.isLoggedIn, values.isLoggedOutPath);
        if(values.tenant && values.isLoggedIn && values.isLoggedOutPath === false){
            //if tenant is loaded, load the tenant theme
            themeToLoad = values.tenant.themeName || this.theme.defaultTheme;
        }
        if(this.isLoadingTheme === themeToLoad){
            return;
        }

        //if the theme is allready loaded stop loading
        if(values.themeConfig && values.themeConfig.name === themeToLoad){
            return;
        }

        this.stateService.change(States.themeIsLoading, true);
        this.isLoadingTheme = themeToLoad;
        const result = await this.theme.loadTheme(themeToLoad);
        this.stateService.change(States.showCookieNotification, result.cookieNotice);
        this.stateService.change(States.themeIsLoading, false);
    }

    //check if the the site should be refreshed
    @StateListener([States.refreshPage, States.isTokenRefreshing])
    public async checkPageRefresh(values: any){
        if(values.refreshPage && !values.isTokenRefreshing){
            const refreshTimeLimit = 20000;
            this.stateService.change(States.refreshPage, false);
            const lastReload = parseInt(localStorage.getItem('_lastreload'), 10) || new Date().getTime();
            let reloadCounter = parseInt(localStorage.getItem('_reloadcounter'), 10);

            //checks if there where more then 5 reloads in the last 20 seconds
            if(!reloadCounter || (lastReload + refreshTimeLimit < new Date().getTime())){
                reloadCounter = 1;
                localStorage.setItem('_reloadcounter', String(reloadCounter));
            }

            if(reloadCounter < 6){
                localStorage.setItem('_lastreload', String(new Date().getTime()));
                localStorage.setItem('_reloadcounter', String(++reloadCounter));
                console.info('Reload [InitWorkflowService.checkPageRefresh]');
                location.reload();
            }else{
                console.info('To much reloads [InitWorkflowService.checkPageRefresh]');
                localStorage.setItem('_reloadcounter', String(0));
            }
        }
    }

    //check if tracking is allowed, if so add the tracking scripts
    @StateListener([States.trackingAllowed], { init : true, unsubscribeFalsy: true })
    public async checkIfTrackingIsAllowed(values: any){
        const win = window as any;
        if(values.trackingAllowed){
            //if tracking is allowed add tracking pixel
            const trackingScriptConfig = document.createElement('script');
            trackingScriptConfig.setAttribute('type', 'text/javascript');
            trackingScriptConfig.setAttribute('src', 'https://w3.siemens.com/ote/ote_config.js');
            const trackingScript = document.createElement('script');
            trackingScript.setAttribute('type', 'text/javascript');
            trackingScript.setAttribute('src', 'https://w3.siemens.com/osspad3/ote/regions/ote.js');
            trackingScript.onload = () => {
                this.stateService.change(States.trackingLoaded, true);
            }
            document.querySelector('html').appendChild(trackingScriptConfig);
            document.querySelector('html').appendChild(trackingScript);
            return true;
        }
        return false;
    }


    //check if tenant has changed
    @StateListener([States.tenantChanged])
    public async onTenantChanged(tenant){
        const params = this.routingInfo.getCurrentRoutingParams();
        if(!Object.keys(params).length){
            return;
        }
        let config = this.routingInfo.getCurrentConfig();
        let lastChecked;
        while(config){
            if(!config.path.includes(':')){
                if(!lastChecked) {
                    lastChecked = config;
                }
            }else{
                lastChecked = null;
            }
            config = config.parent;
        }

        if(lastChecked){
            const path = this.routingInfo.getFullPathFromConfig(lastChecked);
            if(path){
                this.router.navigateByUrl(path);
            }
        }
    }

    // set the operating landing pages for the tenant
    private lastOperatingLandingPageOption : any;
    @StateListener([States.tenant])
    public async checkTenantNaviation(tenant) {
        if(tenant?.tenant?.operatingLandingPageOption !== this.lastOperatingLandingPageOption) {
            this.routingModifiyer.setPlantAndSystemRedirects(tenant?.tenant?.operatingLandingPageOption)
            this.lastOperatingLandingPageOption = tenant?.tenant?.operatingLandingPageOption;
        }
    }

    @StateListener([States.isLoggedIn])
    public async checkLogin(data){
        if(!data.isLoggedIn) {
            // reset routing origins
            this.routingModifiyer.resetOrigins();
            this.fullsScreenService.normalScreen()
            // if the current theme is not the default theme refresh
            const currentTheme = this.themeService.currentThemeConfig?.themeName;
            const detectedTheme = this.config.serverConfig.DOMAIN_THEME;
            if(currentTheme && detectedTheme && detectedTheme !== currentTheme){
                // reload because themes are different
                this.loader.start();
                const appEls = document.getElementsByTagName('cl-app');
                if (appEls.length > 0) {
                    (appEls[0] as HTMLElement).style.display = 'none'
                }
                location.reload();
            }
        }
    }

    @StateListener([States.currentRoutingConfig])
    public async checkPageLyout(data) {
        if(data?.currentRoutingConfig?.pageLayout) {
            document.body.setAttribute('layout', data.currentRoutingConfig.pageLayout);
        }else{
            document.body.removeAttribute('layout');
        }
    }

}
